
import GeoJSON from 'ol/format/GeoJSON';
import ImageLayer from 'ol/layer/Image';
import ImageWMS from 'ol/source/ImageWMS.js';
import {bbox as bboxStrategy} from 'ol/loadingstrategy';
import Projection from 'ol/proj/Projection';
import {get as getProjection} from 'ol/proj';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS.js';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import WMTS from 'ol/source/WMTS';
import TileGrid from 'ol/tilegrid/TileGrid';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import XYZ from 'ol/source/XYZ';
import TileDebug from 'ol/source/TileDebug';

import { createStyle } from './olStyles';

const SOURCE_CLASSES = {
    'GeoJson': {
        layer: VectorLayer
    },
    'ImageWMS': {
        layer: ImageLayer,
        source: ImageWMS
    },
    'TileWMS': {
        layer: TileLayer,
        source: TileWMS
    },
    'TileXYZ': {
        layer: TileLayer
    },
    'Vector': {
        layer: VectorLayer
    },
    'WMTS': {
        layer: TileLayer
    }
};

function createLayer(layer) {
    let layerOptions = createLayerOptions(layer);
    return new SOURCE_CLASSES[layer.type].layer(layerOptions);
}

function createLayerOptions(layer) {
    switch (layer.type) {
        case 'Vector':
            return createLayerOptionsGeoJson(layer);
        case 'GeoJson':
            return createLayerOptionsGeoJson(layer);
        default:
            return createLayerOptionsDefault(layer);
    }
}

function createLayerOptionsDefault(layer) {
    return {
        id: layer.id,
        maxResolution: layer.maxResolution,
        minResolution: layer.minResolution,
        opacity: layer.opacity || 1.0,
        source: createLayerSource(layer)
    };
}

function createLayerOptionsGeoJson(layer) {
    let options = createLayerOptionsDefault(layer);
    if (layer.style) {
        if (layer.style.default) {
            options.style = createStyle(layer.style.default);
        }
        if (layer.style.highlight) {
            options.highlightStyle = createStyle(layer.style.highlight);
        }
    }
    return options;
}

function createLayers(layers) {
    return layers.map(item => {
        let layer = createLayer(item);
        return layer;
    });
}

function createLayerSource(layer) {
    switch (layer.type) {
        case 'GeoJson':
            return createLayerSourceGeoJson(layer);
        case 'TileXYZ':
            return createLayerSourceTileXYZ(layer);
        case 'Vector':
            return createLayerSourceVector(layer);
        case 'WMTS':
            return createLayerSourceWmts(layer);
        default:
            let sourceOptions = {
                ...layer.source,
                crossOrigin: 'Anonymous'
            };
            return new SOURCE_CLASSES[layer.type].source(sourceOptions);
    }
}

function createLayerSourceGeoJson(layer) {
    let source = new VectorSource({
        ...layer.source,
        format: new GeoJSON(),
        url: function(extent, resolution, projection) {
            return layer.source.url + '&bbox=' + extent.join(',');
        },
        strategy: bboxStrategy,
    });
    source.on('addfeature', (evt) => {
        if (!evt.feature.layerId) {
            evt.feature.layerId = layer.id;
        }
    });
    return source;
}

function createLayerSourceTileXYZ(layer) {
    const tileGrid = new TileGrid({
        ...layer.source.tileGrid,
        projection: getProjection(layer.source.tileGrid.projection)
    });
    if (layer.source.params.debug === true) {
        return new TileDebug({
            tileGrid: tileGrid
        });
    } else {
        return new XYZ({
            ...layer.source.params,
            crossOrigin: 'Anonymous',
            tileGrid: tileGrid
        });
    }
}

function createLayerSourceVector(layer) {
    let source = new VectorSource({
        ...layer.source
    });
    source.on('addfeature', (evt) => {
        if (!evt.feature.layerId) {
            evt.feature.layerId = layer.id;
        }
    });
    return source;
}

function createLayerSourceWmts(layer) {
    return new WMTS({
        ...layer.source.params,
        crossOrigin: 'Anonymous',
        tileGrid: new WMTSTileGrid({
            ...layer.source.tileGrid,
            projection: getProjection(layer.source.tileGrid.projection)
        })
    });
}

function createProjection(projectionCode) {
    return new Projection({
        code: projectionCode,
        units: 'm'
    });
}

function getLayerIdFromFeature(feature) {
    return feature.layerId;
}

function getLayerIdFromFeatureForData(feature) {
    return feature.layerIdData || feature.layerId;
}

function getLayerById(map, layerId) {
    let layer;
    map.getLayers().forEach(item => {
        if (item.get('id') === layerId) {
            layer = item;
        }
    });
    return layer;
}

export {
    createLayer,
    createLayers,
    createProjection,
    getLayerById,
    getLayerIdFromFeature,
    getLayerIdFromFeatureForData
}
